import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
	PetSpeciesEnum,
	resolveSpeciesFormatting,
} from '@src/components/PatientInformation/PatientInformation.helpers';
import type { EssentialsPlan } from '@src/components/UserPortal/UserPortal.helpers';
import { Patient } from '@src/lib/__generated/vetspire/sdk';
import { mountDevTools } from '@src/store/utils/mount-dev-tools';

// Extend the generated Patient with the optional plan.
export type PetInformationState = Patient & { essentialsPlan?: EssentialsPlan };

type PetsInformationState = {
	pets: Map<string, PetInformationState>;
	selectedPet: string | null;
};

type PetsInformationActions = {
	addPet: (petInformation: PetInformationState) => void;
	getPet: (petId: string) => PetInformationState | undefined;
	getSelectedPet: () => PetInformationState | undefined;
	setAllPets: (petsInfo: Partial<PetInformationState>[]) => void;
	setSelectedPet: (petId: string | null) => void;
	reset: () => void;
	createPet: (petInformation: PetInformationState) => void;
	updatePet: (petId: string, petInformation: Partial<PetInformationState>) => void;
	getInactivePets: () => PetInformationState[];
	getActivePets: () => PetInformationState[];
	getActiveDogsAndCats: () => PetInformationState[];
	setEssentialPlansByPatientId: (plansById: Record<string, EssentialsPlan>) => void;
};

const INITIAL_STATE: PetsInformationState = { pets: new Map(), selectedPet: null };

const usePetsInformationStore = create<PetsInformationState & PetsInformationActions>()(
	persist(
		(set) => ({
			...INITIAL_STATE,
			addPet: (petInformation: PetInformationState) => {
				set((prevState) => {
					const pets = new Map(prevState.pets);
					if (petInformation.id && !prevState.pets.has(petInformation.id)) {
						pets.set(petInformation.id, petInformation);
					}
					return { ...prevState, pets };
				});
			},
			getSelectedPet: () => {
				const state = usePetsInformationStore.getState();
				const { selectedPet, pets } = state;

				// if user has no pets
				if (pets.size === 0) {
					return {};
				}

				// if no pet has been selected or if it does not exist, default to the first active
				// (non-deceased) pet. Fall back to the first pet overall if no active pets exist.
				if (!selectedPet || !pets.has(selectedPet)) {
					const petsArray = Array.from(pets.values());
					const firstActivePet = petsArray.find((pet) => pet.isActive && !pet.isDeceased);
					const defaultPet = firstActivePet ?? petsArray[0];
					set({ selectedPet: defaultPet?.id ?? petsArray[0].id });
					return defaultPet;
				}

				return pets.get(selectedPet);
			},
			getPet: (petId: string) => {
				const state = usePetsInformationStore.getState();
				return state.pets.get(petId);
			},
			setAllPets: (petsInfo: Partial<PetInformationState>[]) => {
				const pets = new Map();
				petsInfo.forEach((pet) => {
					pets.set(pet.id, { ...pet, species: resolveSpeciesFormatting(pet?.species ?? '') });
				});
				set((prevState) => ({ ...prevState, pets }));
			},
			setSelectedPet: (petId: string | null) => {
				set({ selectedPet: petId });
			},
			reset: () => set(INITIAL_STATE),
			createPet: (petInformation: PetInformationState) => {
				set((prevState) => {
					const pets = new Map(prevState.pets);
					if (petInformation.id && !prevState.pets.has(petInformation.id)) {
						pets.set(petInformation.id, petInformation);
					}
					return { ...prevState, pets };
				});
			},
			updatePet: (petId: string, petInformation: Partial<PetInformationState>) => {
				set((prevState) => {
					const pets = new Map(prevState.pets);
					const existingPetInfo = pets.get(petId);
					if (!existingPetInfo) return prevState; // nothing to update
					const updatedPetInfo = { ...existingPetInfo, ...petInformation } as Patient;
					pets.set(petId, updatedPetInfo);
					return { ...prevState, pets };
				});
			},
			getInactivePets: () => {
				const state = usePetsInformationStore.getState();
				return Array.from(state.pets.values())
					.filter((pet): pet is PetInformationState => !!pet)
					.filter((pet) => !pet.isActive || pet.isDeceased);
			},
			getActivePets: () => {
				const state = usePetsInformationStore.getState();
				return Array.from(state.pets.values())
					.filter((pet): pet is PetInformationState => !!pet)
					.filter((pet) => pet.isActive && !pet.isDeceased);
			},
			getActiveDogsAndCats: () => {
				const activePets = usePetsInformationStore.getState().getActivePets();
				return activePets.filter(
					(pet) => pet.species === PetSpeciesEnum.Dog || pet.species === PetSpeciesEnum.Cat
				);
			},
			setEssentialPlansByPatientId: (plansById) =>
				set((prevState) => {
					const pets = new Map(prevState.pets);
					pets.forEach((pet, id) => {
						const plan = plansById[String(id)];
						if (plan) {
							pets.set(id, { ...pet, essentialsPlan: plan });
						}
					});
					return { ...prevState, pets };
				}),
		}),
		{
			name: 'book-an-appointment-pets-information',
			storage: {
				getItem: (name) => {
					const str = sessionStorage.getItem(name);
					if (!str) return null;
					const data = JSON.parse(str);
					if (data.state && Array.isArray(data.state.pets)) {
						data.state.pets = new Map(data.state.pets);
					}
					return data;
				},
				setItem: (name, value) => {
					const petsArray = Array.from(value.state.pets.entries());
					sessionStorage.setItem(
						name,
						JSON.stringify({ ...value, state: { ...value.state, pets: petsArray } })
					);
				},
				removeItem: (name) => sessionStorage.removeItem(name),
			},
		}
	)
);

mountDevTools('usePetsInformationStore', usePetsInformationStore);

export default usePetsInformationStore;
